import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Dropzone } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useAdminCreateReferenceService,
  useAdminCreateUpdateService,
  useAdminCreateVersionService,
  useAdminSourcesService,
  useAdminUploadVersionFileService,
} from '@/services/backoffice';
import { cn } from '@/utils/classUtils';
import { VERSION_STATUSES } from '@ditto/shared';
import { Check } from 'lucide-react';
import { useState, type FC } from 'react';
import { DatePickerInput } from './DatePickerInput';
import { SourceCombobox } from './SourceCombobox';

interface CreateReferenceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'reference' | 'version' | 'update';

const STEPS: { key: Step; label: string; subtitle: string }[] = [
  { key: 'reference', label: 'Reference', subtitle: 'Reference information' },
  { key: 'version', label: 'Version', subtitle: 'Version details' },
  { key: 'update', label: 'Update', subtitle: 'Update details' },
];

export const CreateReferenceModal: FC<CreateReferenceModalProps> = ({
  open,
  onOpenChange,
}) => {
  // Step
  const [step, setStep] = useState<Step>('reference');

  // Reference fields
  const [externalId, setExternalId] = useState('');
  const [title, setTitle] = useState('');
  const [referenceType, setReferenceType] = useState('');
  const [source, setSource] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');

  // Version fields
  const [versionStatus, setVersionStatus] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [versionUrl, setVersionUrl] = useState('');
  const [versionFile, setVersionFile] = useState<File | null>(null);

  // Update fields
  const [createUpdate, setCreateUpdate] = useState(true);
  const [publicationDate, setPublicationDate] = useState('');
  const [summary, setSummary] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createReference } = useAdminCreateReferenceService();
  const { createVersion } = useAdminCreateVersionService();
  const { createUpdate: createUpdateMutation } = useAdminCreateUpdateService();
  const { uploadFile } = useAdminUploadVersionFileService();
  const { sources } = useAdminSourcesService();

  const resetForm = () => {
    setStep('reference');
    setExternalId('');
    setTitle('');
    setReferenceType('');
    setSource('');
    setUrl('');
    setDescription('');
    setVersionStatus('');
    setEffectiveDate('');
    setVersionUrl('');
    setVersionFile(null);
    setCreateUpdate(true);
    setPublicationDate('');
    setSummary('');
    setIsSubmitting(false);
  };

  const stepIndex = STEPS.findIndex(s => s.key === step);

  const isReferenceValid =
    !!externalId && !!title && !!referenceType && !!source;
  const isVersionValid = !!versionStatus;

  const canGoNext =
    (step === 'reference' && isReferenceValid) ||
    (step === 'version' && isVersionValid) ||
    step === 'update';

  const handleNext = () => {
    if (step === 'reference') setStep('version');
    else if (step === 'version') setStep('update');
  };

  const handleBack = () => {
    if (step === 'version') setStep('reference');
    else if (step === 'update') setStep('version');
  };

  const handleSubmit = () => {
    if (!isReferenceValid || !isVersionValid) return;
    setIsSubmitting(true);

    // Step 1: Create reference
    createReference(
      {
        externalId,
        title,
        type: referenceType,
        source,
        url: url || undefined,
        description: description || undefined,
      },
      {
        onSuccess: refResult => {
          const refId = refResult.id;

          // Step 2: Create version
          createVersion(
            {
              referenceId: refId,
              status: versionStatus,
              effectiveDate: effectiveDate || undefined,
              url: versionUrl || undefined,
            },
            {
              onSuccess: versionResult => {
                const verId = versionResult.id;

                // Step 2b: Upload file if provided
                const afterFileUpload = () => {
                  // Step 3: Create update if checked
                  if (createUpdate) {
                    createUpdateMutation(
                      {
                        referenceId: refId,
                        versionId: verId,
                        updateType: 'new',
                        summary: summary || undefined,
                        publicationDate: publicationDate || undefined,
                      },
                      {
                        onSuccess: () => {
                          setIsSubmitting(false);
                          onOpenChange(false);
                          resetForm();
                        },
                        onError: () => {
                          setIsSubmitting(false);
                        },
                      }
                    );
                  } else {
                    setIsSubmitting(false);
                    onOpenChange(false);
                    resetForm();
                  }
                };

                if (versionFile) {
                  uploadFile(
                    { versionId: verId, file: versionFile },
                    {
                      onSuccess: () => afterFileUpload(),
                      onError: () => {
                        setIsSubmitting(false);
                      },
                    }
                  );
                } else {
                  afterFileUpload();
                }
              },
              onError: () => {
                setIsSubmitting(false);
              },
            }
          );
        },
        onError: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        onOpenChange(val);
        if (!val) resetForm();
      }}
    >
      <DialogContent className="max-w-4xl min-h-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Reference</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Step {stepIndex + 1} of {STEPS.length} : {STEPS[stepIndex].subtitle}
          </p>
        </DialogHeader>

        {/* Step indicator */}
        <div className="border-y py-4 -mx-6 px-6">
          <div className="flex items-center justify-center gap-8">
            {STEPS.map((s, i) => {
              const isCompleted = i < stepIndex;
              const isCurrent = s.key === step;
              return (
                <div key={s.key} className="flex items-center gap-2.5">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium border',
                      isCurrent &&
                        'border-primary bg-primary text-primary-foreground',
                      isCompleted &&
                        'border-primary bg-primary text-primary-foreground',
                      !isCurrent &&
                        !isCompleted &&
                        'border-border text-muted-foreground'
                    )}
                  >
                    {isCompleted ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span
                    className={cn(
                      'text-sm',
                      isCurrent && 'font-medium',
                      !isCurrent && !isCompleted && 'text-muted-foreground'
                    )}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'reference' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>External ID</Label>
                  <Input
                    placeholder="e.g. ISO 13485"
                    value={externalId}
                    onChange={e => setExternalId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    placeholder="Reference title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={referenceType}
                    onValueChange={setReferenceType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regulation">Regulation</SelectItem>
                      <SelectItem value="guidance">Guidance</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="news">News</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <SourceCombobox
                    sources={sources}
                    value={source}
                    onChange={setSource}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL (optional)</Label>
                <Input
                  placeholder="https://..."
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  placeholder="Optional description..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="min-h-[200px]"
                />
              </div>
            </div>
          )}

          {step === 'version' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={versionStatus}
                    onValueChange={setVersionStatus}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {VERSION_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Effective Date (optional)</Label>
                  <DatePickerInput
                    value={effectiveDate}
                    onChange={setEffectiveDate}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL (optional)</Label>
                <Input
                  placeholder="https://..."
                  value={versionUrl}
                  onChange={e => setVersionUrl(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Document (optional)</Label>
                {versionFile ? (
                  <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
                    <span className="flex-1 text-sm truncate">
                      {versionFile.name}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVersionFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Dropzone
                    onFilesSelected={files => {
                      if (files[0]) setVersionFile(files[0]);
                    }}
                    title="Drop PDF here"
                    subtitle="or click to browse"
                    buttonText="Browse"
                    accept=".pdf,application/pdf"
                    multiple={false}
                    className="min-h-[200px]"
                  />
                )}
              </div>
            </div>
          )}

          {step === 'update' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Create a new update</p>
                  <p className="text-sm text-muted-foreground">
                    Create an update of type &ldquo;new&rdquo; that will appear
                    in the feed of users subscribed to this source.
                  </p>
                </div>
                <Switch
                  checked={createUpdate}
                  onCheckedChange={setCreateUpdate}
                />
              </div>

              <div
                className={cn(
                  'space-y-4 transition-opacity',
                  !createUpdate && 'opacity-40 pointer-events-none'
                )}
              >
                <div className="space-y-2">
                  <Label>Publication Date (optional)</Label>
                  <DatePickerInput
                    value={publicationDate}
                    onChange={setPublicationDate}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Summary (optional)</Label>
                  <Textarea
                    placeholder="Update summary..."
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    className="min-h-[200px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div>
            {stepIndex > 0 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {step !== 'update' ? (
              <Button onClick={handleNext} disabled={!canGoNext}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isReferenceValid || !isVersionValid || isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
